import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { DialogService } from '../dialog/dialog.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { NavigationService } from '../navigation/navigation.service';
import { InvoiceDataStore } from './invoice-data.store';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { Invoice } from './invoice.model';
import { DocumentDataEffects } from '../document-data/document-data.effects';
import { InvoiceDataService } from './invoice-data.service';
import { IState } from './invoice-data.reducer';
import { InvoiceDataBuilder } from './invoice-data.builder';
import { ActionsEnum as AppActionsEnum, AppStore } from '../../store';
import { UtilsService } from '../shared/services/utils/utils.service';
import { RendererService } from '../renderer/renderer.service';
import {
  catchError,
  concatMap,
  delay,
  exhaustMap,
  filter,
  finalize,
  flatMap,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ActionsEnum,
  batchMarkInvoiceAsPaidNetwork,
  BatchMarkInvoiceAsPaidNetwork,
  batchMarkInvoiceAsPaidNetworkError,
  BatchMarkInvoiceAsPaidNetworkError,
  batchMarkInvoiceAsPaidNetworkSuccess,
  BatchMarkInvoiceAsPaidNetworkSuccess,
  batchMarkInvoiceAsPaidStore,
  BatchMarkInvoiceAsPaidStore,
  CreateManualPayment,
  CreateManualPaymentError,
  createManualPaymentError,
  CreateManualPaymentSuccess,
  createManualPaymentSuccess,
  DeleteManualPayment,
  DeleteSchedule,
  deleteScheduleError,
  DeleteScheduleError,
  fetchSchedules,
  FetchSchedules,
  FetchSchedulesError,
  fetchSchedulesError,
  MarkAsPaid,
  MarkAsPaidError,
  markAsPaidError,
  MarkAsPaidSuccessV2,
  markAsPaidSuccessV2,
  MarkSelectedInvoicesAsPaid,
  RefundEPayment,
  SetSchedulesList,
  setSchedulesList,
  transactionsUpdateSuccess,
  TransactionsUpdateSuccess,
  UnmatchTransaction,
  UpdateSchedule,
  updateScheduleError,
  UpdateScheduleError,
} from './invoice-data.actions';
import {
  ActionsEnum as EntityActionsEnum,
  batchSaveEntityStore,
  BatchSaveEntityStore,
  FetchFilters,
  fetchFilters,
  RefreshEditingEntity,
  refreshEditingEntity,
  ResetSelectedList,
  resetSelectedList,
  saveEditingEntity,
  SaveEntityNetworkSuccess,
  SaveEntityStore,
  saveEntityStore,
  setDispatchedResponse,
  SetEditingEntity,
  setEditingEntity,
  setSelectedList,
  SetSelectedList,
} from '../entity-data/entity-data.actions';
import { concat, forkJoin, merge, Observable, of } from 'rxjs';
import { IResponseInfo } from '../http/http.data';
import { v4 } from 'uuid';
import { DocumentOverallStatesEnum } from '../document-data/document-data.data';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { compact } from 'lodash';
import * as fromPayments from '../payments-data/payments-data.actions';
import * as fromToast from '../toaster/toaster.actions';
import { showToast, ShowToast } from '../toaster/toaster.actions';
import { ManualTransaction } from 'i2g-yakka';
import { Settings } from '../settings-data/settings.model';
import { DIALOG_TYPE as MarkAsPaidDialog } from '../payments/components/mark-as-paid-dialog/mark-as-paid-dialog.component';
import { PaymentTransaction } from '../document-data/sub-models/payment-transaction.model';
import { InvoiceFiltersEnum, IScheduleResponse } from './invoice-data.data';
import { List, Set } from 'immutable';
import { Schedule } from '../document-data/sub-models/schedule.model';
import { Document } from '../document-data/document.model';
import { DocumentPayments } from '../document-data/sub-models/document-payments.model';
import { PaymentErrorsEnum } from '../payments-data/payments-data.data';
import { AccountDataStore } from '../account-data/account-data.store';
import {
  HomeDataStore,
  setTaskItemState,
  SetTaskItemState,
} from '../home-data';
import { TaskItem } from '../home-data/models/submodels/task-item.model';
import {
  TaskItemStateEnum,
  TaskItemTypeEnum,
} from '../home-data/home-data.data';
import { DateTimeService } from 'i2g-ng-utils';

@Injectable()
export class InvoiceDataEffects extends DocumentDataEffects<IState, Invoice> {
  constructor(
    private _invoiceDataService: InvoiceDataService,
    private _dateTimeService: DateTimeService,
    private _invoiceDataBuilder: InvoiceDataBuilder,
    private _homeDataStore: HomeDataStore,
    invoiceDataStore: InvoiceDataStore,
    accountDataStore: AccountDataStore,
    utilsService: UtilsService,
    rendererService: RendererService,
    actions: Actions,
    appStore: AppStore,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      _invoiceDataService,
      invoiceDataStore,
      _invoiceDataBuilder,
      accountDataStore,
      utilsService,
      rendererService,
      actions,
      appStore,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() fetchFilters = this.fetchFilters;
  @Effect() triggerRefreshFiltersWithDelay = this
    .triggerRefreshFiltersWithDelay;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() deleteSelectedEntities = this.deleteSelectedEntities;
  @Effect() batchDeleteEntityStore = this.batchDeleteEntityStore;
  @Effect() batchDeleteEntityNetwork = this.batchDeleteEntityNetwork;
  @Effect() batchSaveEntityStore = this.batchSaveEntityStore;
  @Effect() clearSelectedEntitiesOnFetchOrSearchList = this
    .clearSelectedEntitiesOnFetchOrSearchList;
  @Effect() addToLimitedSelectedList = this.addToLimitedSelectedList;
  @Effect() hideBulkLimitBanner = this.hideBulkLimitBanner;
  @Effect() printDocument = this.printDocument;
  @Effect() markAsPrinted = this.markAsPrinted;
  @Effect() getEntityById = this.getEntityById;
  @Effect() copyDocument = this.copyDocument;
  @Effect() saveCopiedDocument = this.saveCopiedDocument;
  @Effect() redirectToCopiedDocument = this.redirectToCopiedDocument;
  @Effect() enableCardPayments = this.enableCardPayments;
  @Effect() sendDocument = this.sendDocument;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() createNewEditingEntity = this.createNewEditingEntity;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;
  @Effect() addDocumentItemToEditing = this.addDocumentItemToEditing;
  @Effect() updateDocumentItemInEditing = this.updateDocumentItemInEditing;
  @Effect() deleteDocumentItemFromEditing = this.deleteDocumentItemFromEditing;
  @Effect() moveDocumentItemInEditing = this.moveDocumentItemInEditing;
  @Effect() batchAddDocumentItemsToEditing = this
    .batchAddDocumentItemsToEditing;
  @Effect() updateTaxInEditing = this.updateTaxInEditing;
  @Effect() refreshListAndFiltersOnTaxYearChange = this
    .refreshListAndFiltersOnTaxYearChange;
  @Effect({ dispatch: false }) goToDocumentAddClientScreen = this
    .goToDocumentAddClientScreen;

  @Effect()
  onInitInvoiceStore: Observable<FetchSchedules> = this.actions.pipe(
    ofType(AppActionsEnum.INIT_APP),
    take(1),
    map(() => fetchSchedules())
  );

  @Effect()
  fetchSchedules: Observable<
    SetSchedulesList | FetchSchedulesError
  > = this.actions.pipe(
    ofType(ActionsEnum.FETCH_SCHEDULES),
    switchMap(() => {
      return this._invoiceDataService.fetchSchedules().pipe(
        map(({ schedules, deletedIds }: IScheduleResponse) =>
          setSchedulesList(this.entityType, schedules, deletedIds)
        ),
        catchError(() => of(fetchSchedulesError()))
      );
    })
  );

  @Effect()
  updateSchedule: Observable<
    SetSchedulesList | UpdateScheduleError
  > = this.actions.pipe(
    ofType(ActionsEnum.UPDATE_SCHEDULE),
    switchMap(({ payload }: UpdateSchedule) => {
      const { documentId, properties } = payload;
      return this._invoiceDataService
        .updateSchedule(documentId, properties)
        .pipe(
          map((schedule: Schedule) =>
            setSchedulesList(this.entityType, List([schedule]), List([]))
          ),
          catchError(() => of(updateScheduleError()))
        );
    })
  );

  @Effect()
  deleteSchedule: Observable<
    SetSchedulesList | DeleteScheduleError
  > = this.actions.pipe(
    ofType(ActionsEnum.DELETE_SCHEDULE),
    switchMap(({ payload }: DeleteSchedule) => {
      const { documentId } = payload;
      return this._invoiceDataService.deleteSchedule(documentId).pipe(
        map(() =>
          setSchedulesList(this.entityType, List([]), List([documentId]))
        ),
        catchError(() => of(deleteScheduleError()))
      );
    })
  );

  @Effect()
  createManualPayment: Observable<
    | SaveEntityStore
    | SetEditingEntity
    | CreateManualPaymentSuccess
    | CreateManualPaymentError
  > = this.actions.pipe(
    ofType(ActionsEnum.CREATE_MANUAL_PAYMENT),
    filter(
      ({ payload }: CreateManualPayment) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: CreateManualPayment) => {
      const { dispatchId, addManualPaymentData, setEditingDocument } = payload;
      const {
        document,
        addPayment,
        sendReceipt,
        renderingOptions,
      } = addManualPaymentData;

      this.dialogService.openLoadingDialog();

      return this._invoiceDataService
        .addManualPayment(document.id, addPayment)
        .pipe(
          flatMap(({ body }: IResponseInfo) => {
            const effects = [];
            const transactionId = body.created_transaction.transaction_id;
            const isNew = document.isNew();
            const invoiceProps = this.utilsService.addManualTransaction(
              document,
              addPayment
            );
            const newInvoice = this._invoiceDataBuilder.buildEntity(
              invoiceProps,
              null,
              false,
              isNew,
              true
            );
            const recipient = document.billingName || document.billingEmail;

            effects.push(saveEntityStore(this.entityType, newInvoice, isNew));

            if (setEditingDocument) {
              effects.push(
                setEditingEntity(this.entityType, newInvoice),
                saveEditingEntity(v4(), this.entityType)
              );
            }

            effects.push(
              createManualPaymentSuccess(this.entityType, newInvoice),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                ActionsEnum.CREATE_MANUAL_PAYMENT,
                true,
                newInvoice
              )
            );

            if (sendReceipt) {
              effects.push(
                fromPayments.sendReceipt(
                  v4(),
                  document.id,
                  recipient,
                  transactionId,
                  renderingOptions,
                  false
                )
              );
            }

            return concat(effects);
          }),
          finalize(() => this.dialogService.closeLoadingDialog()),
          catchError((error) =>
            concat([
              createManualPaymentError(
                this.entityType,
                new Error('create manual payment error'),
                error
              ),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                ActionsEnum.CREATE_MANUAL_PAYMENT,
                false,
                document,
                error
              ),
            ])
          )
        );
    })
  );

  @Effect()
  createManualPaymentSuccess: Observable<
    fromToast.ShowToast | FetchFilters
  > = this.actions.pipe(
    ofType(ActionsEnum.CREATE_MANUAL_PAYMENT_SUCCESS),
    filter(
      ({ payload }: CreateManualPaymentSuccess) =>
        payload.entityType === this.entityType
    ),
    filter(({ payload }: CreateManualPaymentSuccess) => {
      const { invoice } = payload;

      return invoice.states.overall === DocumentOverallStatesEnum.fully_paid;
    }),
    flatMap(({ payload }: CreateManualPaymentSuccess) => {
      const { invoice } = payload;

      return concat([
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.unpaid
        ),
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.paid
        ),
        fromToast.showToast(
          this._buildMarkAsPaidToastConfig(
            invoice.docNumber,
            invoice.billingName
          )
        ),
      ]);
    })
  );

  @Effect()
  deleteManualPayment: Observable<
    SetEditingEntity | SaveEntityStore | TransactionsUpdateSuccess
  > = this.actions.pipe(
    ofType(ActionsEnum.DELETE_MANUAL_PAYMENT),
    withLatestFrom(this.documentDataStore.getEditingEntity()),
    filter(
      ([{ payload }, document]: [DeleteManualPayment, Document]) =>
        payload.entityType === this.entityType && Boolean(document)
    ),
    flatMap(([{ payload }, document]: [DeleteManualPayment, Document]) => {
      const { transactionId } = payload.paymentTransaction;

      this.dialogService.openLoadingDialog();

      return this._invoiceDataService
        .deleteManualPayment(document.id, transactionId)
        .pipe(
          flatMap(() => this.settingsDataStore.getSettings()),
          tap(() => this.dialogService.closeLoadingDialog()),
          flatMap((settings: Settings) => {
            const transactions = document.payments.transactions
              .filterNot(
                (tx: PaymentTransaction) => tx.transactionId === transactionId
              )
              .toList();
            const newDocumentProps = document.setIn(
              [Document.KEYS.PAYMENTS, DocumentPayments.KEYS.TRANSACTIONS],
              transactions
            );
            const newDocument = this.documentDataBuilder.buildEntity(
              newDocumentProps,
              settings,
              true,
              document.isNew(),
              true
            );

            return concat([
              setEditingEntity(this.entityType, newDocument, true),
              saveEntityStore(this.entityType, newDocument, false),
              transactionsUpdateSuccess(
                this.entityType,
                newDocument,
                ActionsEnum.DELETE_MANUAL_PAYMENT
              ),
            ]);
          })
        );
    })
  );

  @Effect()
  markAsPaid: Observable<
    SaveEntityStore | SetEditingEntity | MarkAsPaidSuccessV2 | MarkAsPaidError
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_AS_PAID),
    filter(({ payload }: MarkAsPaid) => payload.entityType === this.entityType),
    withLatestFrom(this.settingsDataStore.settings$),
    concatMap(([{ payload }, settings]: [MarkAsPaid, Settings]) => {
      const { documentId, setEditingDocument } = payload;

      let markAsPaidData;
      let paymentMethod = null;
      let description = null;
      if (payload.markAsPaidData) {
        markAsPaidData = payload.markAsPaidData;
        this.dialogService.openLoadingDialog();
        paymentMethod = markAsPaidData.paymentMethod;
        description = markAsPaidData.description;
      }

      let transaction: ManualTransaction;
      let fullInvoice: Invoice;

      return this._invoiceDataService.getEntity(documentId).pipe(
        flatMap((fullDocument) => {
          const midDayUtc = this._dateTimeService.getMidDayUtcOfDate(
            new Date().toISOString()
          );
          const outstandingBalance =
            fullDocument.calculationResult.outstandingBalance;

          fullInvoice = fullDocument as Invoice;
          transaction = {
            amount: outstandingBalance < 0 ? 0 : outstandingBalance,
            method_detail: paymentMethod,
            description: description,
            datetime: midDayUtc,
          };

          return this._invoiceDataService.addManualPayment(
            fullDocument.id,
            transaction
          );
        }),
        flatMap(({ body }: IResponseInfo) => {
          const effects = [];
          const transactionId = body.created_transaction.transaction_id;
          const isNew = fullInvoice.isNew();
          const newTransaction = {
            ...transaction,
            transaction_id: transactionId,
          };
          const invoiceProps = this.utilsService.addManualTransaction(
            fullInvoice,
            newTransaction
          );
          const newInvoice = this._invoiceDataBuilder.buildEntity(
            invoiceProps,
            settings,
            false,
            isNew,
            true
          );

          effects.push(
            saveEntityStore(this.entityType, newInvoice, isNew),
            setEditingDocument
              ? setEditingEntity(this.entityType, newInvoice)
              : null,
            markAsPaidSuccessV2(
              this.entityType,
              newInvoice,
              markAsPaidData.isSendReceipt,
              transactionId,
              markAsPaidData.renderingOptions
            )
          );

          return concat(compact(effects));
        }),
        finalize(() => this.dialogService.closeLoadingDialog()),
        catchError((error) =>
          of(
            markAsPaidError(
              this.entityType,
              new Error('mark as paid error'),
              error
            )
          )
        )
      );
    })
  );

  @Effect()
  markAsPaidSuccessV2: Observable<
    fromToast.ShowToast | FetchFilters
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_AS_PAID_SUCCESS_V2),
    filter(
      ({ payload }: MarkAsPaidSuccessV2) =>
        payload.entityType === this.entityType
    ),
    mergeMap(({ payload }: MarkAsPaidSuccessV2) => {
      const {
        invoice,
        isSendReceipt,
        transactionId,
        renderingOptions,
      } = payload;
      const documentId = invoice.id;
      const recipient = invoice.billingName || invoice.billingEmail;

      this.dialogService.closeDialog({ type: MarkAsPaidDialog });
      const effects: any[] = [
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.unpaid
        ),
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.paid
        ),
      ];
      const sendReceipt = fromPayments.sendReceipt(
        v4(),
        documentId,
        recipient,
        transactionId,
        renderingOptions,
        false
      );
      if (isSendReceipt) {
        effects.push(sendReceipt);
      }
      return concat(effects);
    })
  );

  @Effect({ dispatch: false })
  handlePaymentErrors = this.actions.pipe(
    ofType(
      ActionsEnum.CREATE_MANUAL_PAYMENT_ERROR,
      ActionsEnum.MARK_AS_PAID_ERROR
    ),
    filter(
      ({ payload }: CreateManualPaymentError | MarkAsPaidError) =>
        payload.entityType === this.entityType
    ),
    tap(({ payload }: CreateManualPaymentError | MarkAsPaidError) => {
      this.globalBannerService.showSystemErrorBanner(payload.error);
    })
  );

  @Effect()
  refundEPayment: Observable<
    RefreshEditingEntity | ShowToast | TransactionsUpdateSuccess
  > = this.actions.pipe(
    ofType(ActionsEnum.REFUND_E_PAYMENT),
    withLatestFrom(this.documentDataStore.getEditingEntity()),
    filter(
      ([{ payload }, document]: [RefundEPayment, Document]) =>
        payload.entityType === this.entityType && Boolean(document)
    ),
    switchMap(([{ payload }, document]: [RefundEPayment, Document]) => {
      const { transactionId } = payload;

      return this._invoiceDataService
        .refundTransaction(document.id, transactionId)
        .pipe(
          delay(4000), // extra delay so backend has the latest version when entity is refreshed below
          filter((x) => !!x),
          flatMap(() =>
            concat([
              refreshEditingEntity(this.entityType, true),
              transactionsUpdateSuccess(
                this.entityType,
                document,
                ActionsEnum.REFUND_E_PAYMENT
              ),
            ])
          ),
          tap(() => this.dialogService.closeLoadingDialog()),
          catchError((error) => {
            // eslint-disable-next-line
            const [_, errorBody] = error;
            this.dialogService.closeLoadingDialog();

            if (
              !errorBody ||
              errorBody.code !== PaymentErrorsEnum.transaction_refunded
            ) {
              this.globalBannerService.showSystemErrorBanner(error);
              return;
            }

            const toastData: IToasterData = {
              label: this.translateService.instant(
                'TRANSACTION_HISTORY.TRANSACTION_ALREADY_REFUNDED'
              ),
            };
            const errorToastConfig: IToasterConfig = {
              type: 'refund-error',
              component: MessageToastComponent,
              data: toastData,
            };

            return of(fromToast.showToast(errorToastConfig));
          })
        );
    })
  );

  @Effect()
  unmatchTransaction: Observable<
    RefreshEditingEntity | ShowToast | TransactionsUpdateSuccess
  > = this.actions.pipe(
    ofType(ActionsEnum.UNMATCH_TRANSACTION),
    withLatestFrom(this.documentDataStore.getEditingEntity()),
    filter(
      ([{ payload }, document]: [UnmatchTransaction, Document]) =>
        payload.entityType === this.entityType && Boolean(document)
    ),
    switchMap(([{ payload }, document]: [UnmatchTransaction, Document]) => {
      const { transactionId } = payload;

      return this._invoiceDataService
        .unmatchTransaction(document.id, transactionId)
        .pipe(
          delay(4000), // extra delay so backend has the latest version when entity is refreshed below
          filter((x) => !!x),
          flatMap(() =>
            concat([
              refreshEditingEntity(this.entityType, true),
              transactionsUpdateSuccess(
                this.entityType,
                document,
                ActionsEnum.UNMATCH_TRANSACTION
              ),
            ])
          ),
          tap(() => this.dialogService.closeLoadingDialog()),
          catchError(([_, errorBody, error]) => {
            this.dialogService.closeLoadingDialog();

            if (
              !errorBody ||
              errorBody.code !== PaymentErrorsEnum.transaction_unmatched
            ) {
              this.globalBannerService.showSystemErrorBanner(error);
              return;
            }

            const toastData: IToasterData = {
              label: this.translateService.instant(
                'TRANSACTION_HISTORY.TRANSACTION_ALREADY_UNMATCHED'
              ),
            };
            const errorToastConfig: IToasterConfig = {
              type: 'unmatch-error',
              component: MessageToastComponent,
              data: toastData,
            };

            return of(fromToast.showToast(errorToastConfig));
          })
        );
    })
  );

  @Effect()
  transactionsUpdateSuccess: Observable<FetchFilters> = this.actions.pipe(
    ofType(ActionsEnum.TRANSACTIONS_UPDATE_SUCCESS),
    filter(
      ({ payload }: TransactionsUpdateSuccess) =>
        payload.entityType === this.entityType
    ),
    switchMap(() => {
      return concat([
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.unpaid
        ),
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.paid
        ),
      ]);
    })
  );

  @Effect()
  markSelectedInvoicesAsPaid: Observable<
    | BatchMarkInvoiceAsPaidStore
    | ResetSelectedList
    | BatchMarkInvoiceAsPaidNetwork
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_SELECTED_INVOICES_AS_PAID),
    filter(
      ({ payload }: MarkSelectedInvoicesAsPaid) =>
        payload.entityType === this.entityType
    ),
    exhaustMap(({ payload }: MarkSelectedInvoicesAsPaid) => {
      const { listFilter } = payload;

      return this.documentDataStore.getSelectedEntities().pipe(
        take(1),
        filter((x) => !!x),
        flatMap((selectedInvoices: Set<Invoice>) => {
          const unpaidInvoiceList = selectedInvoices
            .filter(
              (invoice: Invoice) =>
                invoice.states.overall !== DocumentOverallStatesEnum.fully_paid
            )
            .toList();

          return concat([
            batchMarkInvoiceAsPaidStore(
              this.entityType,
              unpaidInvoiceList,
              listFilter
            ),
            resetSelectedList(this.entityType),
            batchMarkInvoiceAsPaidNetwork(
              this.entityType,
              unpaidInvoiceList,
              listFilter
            ),
          ]);
        })
      );
    })
  );

  @Effect()
  batchMarkInvoiceAsPaidStore: Observable<BatchSaveEntityStore> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_STORE),
    filter(
      ({ payload }: BatchMarkInvoiceAsPaidStore) =>
        payload.entityType === this.entityType
    ),
    withLatestFrom(this.settingsDataStore.settings$),
    map(([{ payload }, settings]: [BatchMarkInvoiceAsPaidStore, Settings]) => {
      const { invoices, listFilter } = payload;
      const invoicesProps: any = invoices
        .map((invoice: Invoice) => invoice.toJS())
        .toArray();
      const updatedInvoices = List(
        this.utilsService
          .markDocumentsAsPaid(invoicesProps)
          .map((paidInvoice) =>
            this._invoiceDataBuilder.buildEntity(
              paidInvoice,
              settings,
              false,
              false
            )
          )
      );

      return batchSaveEntityStore(this.entityType, updatedInvoices, listFilter);
    })
  );

  @Effect()
  batchMarkInvoiceAsPaidNetwork: Observable<
    | BatchSaveEntityStore
    | SetSelectedList
    | BatchMarkInvoiceAsPaidNetworkSuccess
    | BatchMarkInvoiceAsPaidNetworkError
  > = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK),
    filter(
      ({ payload }: BatchMarkInvoiceAsPaidNetwork) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: BatchMarkInvoiceAsPaidNetwork) => {
      const { invoices, listFilter } = payload;

      // build batch mark as paid requests
      const reqs = invoices
        .map((invoice: Invoice) => {
          const midDayUtc = this._dateTimeService.getMidDayUtcOfDate(
            new Date().toISOString()
          );
          const outstandingBalance =
            invoice.calculationResult.outstandingBalance;
          const transaction = {
            amount: outstandingBalance < 0 ? 0 : outstandingBalance,
            method_detail: null,
            description: null,
            datetime: midDayUtc,
          };

          return this._invoiceDataService
            .addManualPayment(invoice.id, transaction)
            .pipe(
              withLatestFrom(this.settingsDataStore.settings$),
              map(([{ body }, settings]: [IResponseInfo, Settings]) => {
                const transactionId = body.created_transaction.transaction_id;
                const isNew = invoice.isNew();
                const newTransaction = {
                  ...transaction,
                  transaction_id: transactionId,
                };
                const invoiceProps = this.utilsService.addManualTransaction(
                  invoice,
                  newTransaction
                );

                return this._invoiceDataBuilder.buildEntity(
                  invoiceProps,
                  settings,
                  false,
                  isNew,
                  true
                );
              }),
              catchError(() => of(invoice)) // keep invoices that request failed
            );
        })
        .toArray();

      return forkJoin(reqs).pipe(
        flatMap((results: Invoice[]) => {
          const effects = [];
          const grouped = List(results).groupBy(
            (invoice: Invoice) =>
              invoice.states.overall === DocumentOverallStatesEnum.fully_paid
          );
          const successInvoices = List<Invoice>(grouped.get(true));
          const failedInvoices = List<Invoice>(grouped.get(false));

          // if there're failed invoices, return them to list as selected
          if (failedInvoices.size) {
            effects.push(
              batchSaveEntityStore(
                this.entityType,
                failedInvoices as List<any>,
                listFilter
              ),
              setSelectedList(this.entityType, failedInvoices.toSet())
            );
          }

          // if there's at least one success invoice, show toast
          if (successInvoices.size) {
            effects.push(
              batchMarkInvoiceAsPaidNetworkSuccess(
                this.entityType,
                successInvoices
              )
            );
          } else {
            // otherwise display error message
            effects.push(
              batchMarkInvoiceAsPaidNetworkError(
                this.entityType,
                failedInvoices
              )
            );
          }

          return concat(effects);
        }),
        catchError(() =>
          of(batchMarkInvoiceAsPaidNetworkError(this.entityType, invoices))
        )
      );
    })
  );

  @Effect()
  batchMarkInvoiceAsPaidNetworkSuccess: Observable<
    FetchFilters | ShowToast
  > = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS),
    filter(
      ({ payload }: BatchMarkInvoiceAsPaidNetworkSuccess) =>
        payload.entityType === this.entityType
    ),
    flatMap(({ payload }: BatchMarkInvoiceAsPaidNetworkSuccess) => {
      const { invoices } = payload;
      const total = invoices.size;
      const label =
        total === 1
          ? this.translateService.instant(`DOCUMENT.INVOICE.TOAST.PAID`, {
              documentNumber: invoices.first().docNumber,
            })
          : this.translateService.instant(
              `DOCUMENT.INVOICE.TOAST.PAID_MULTIPLE`,
              {
                number: total,
              }
            );
      const toastData: IToasterData = { label, containsHtml: true };
      const toastConfig: IToasterConfig = {
        type: 'bulk-approve-decline-invoices',
        component: MessageToastComponent,
        data: toastData,
      };

      return merge([
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.unpaid
        ),
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          InvoiceFiltersEnum.paid
        ),
        showToast(toastConfig),
      ]);
    })
  );

  @Effect({ dispatch: false })
  batchMarkInvoiceAsPaidNetworkError = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR),
    filter(
      ({ payload }: BatchMarkInvoiceAsPaidNetworkError) =>
        payload.entityType === this.entityType
    ),
    tap(() =>
      this.globalBannerService.showSystemErrorBanner(
        new Error('all invoices failed to be marked as paid')
      )
    )
  );

  /**
   * for FTU task list in home screen, specifically for create first invoice task
   * client needs to tell backend that the task is completed
   */
  @Effect()
  setCreateFirstInvoiceTaskCompleted: Observable<SetTaskItemState> = this.actions.pipe(
    ofType(EntityActionsEnum.SAVE_ENTITY_NETWORK_SUCCESS),
    filter(
      ({ payload }: SaveEntityNetworkSuccess) =>
        payload.entityType === this.entityType &&
        payload.entity.header.isSynced()
    ),
    flatMap(() => {
      return this._homeDataStore
        .getTaskItemByTypeAndState(
          TaskItemTypeEnum.create_first_invoice,
          TaskItemStateEnum.incomplete
        )
        .pipe(
          filter((x) => !!x),
          map((taskItem: TaskItem) =>
            setTaskItemState(taskItem.id, TaskItemStateEnum.completed)
          )
        );
    })
  );

  private _buildMarkAsPaidToastConfig(
    docNumber: string,
    billingName: string
  ): IToasterConfig {
    const label = billingName
      ? this.translateService.instant(
          'DOCUMENT.INVOICE.QUICK_ACTIONS.MARK_AS_PAID',
          {
            document_number: docNumber,
            billing_name: billingName,
          }
        )
      : this.translateService.instant(
          'DOCUMENT.INVOICE.QUICK_ACTIONS.MARK_AS_PAID_NO_BILLING',
          {
            document_number: docNumber,
          }
        );
    const toastData: IToasterData = { label, containsHtml: true };

    return {
      type: 'mark-as-paid',
      component: MessageToastComponent,
      data: toastData,
    };
  }
}
